import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Banknote,
  Building2,
  Clock,
  Mail,
  MapPin,
  Phone,
  Plus,
  Receipt,
  Truck,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { JobStatusBadge, PageHead } from '../components/bits.jsx';
import { AddSiteModal } from '../components/AddSiteModal.jsx';
import { AreaChart } from '../components/charts.jsx';
import { Avatar, Badge, Button, Card, CardBody, CardHead, cx, EmptyState, Field, Input, KeyValue, Loading, Modal, PhoneInput, Stat, Tabs, Textarea, useToast } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { BUCKET_TONE, compactMoney, dateLabel, money, num, phoneDial, relative, titleCase } from '../lib/format.js';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = useApi(`/customers/${id}/detail`, { deps: [id] });
  const [tab, setTab] = useState('overview');
  const [contactOpen, setContactOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);

  if (loading && !data) {
    return (
      <>
        <PageHead title="Customer" subtitle="Loading…" />
        <Loading rows={6} />
      </>
    );
  }
  if (!data?.customer) {
    return <Card><EmptyState icon={Building2} title="Customer not found"><Link to="/app/customers">Back to customers</Link></EmptyState></Card>;
  }

  const { customer, contacts, sites, jobs, invoices, disputes, kpis, monthly } = data;
  const primaryContact = contacts.find((c) => c.isPrimary) || contacts[0];

  return (
    <>
      <PageHead
        breadcrumb={[{ label: 'Customers', to: '/app/customers' }, { label: customer.name }]}
        title={customer.name}
        subtitle={
          <span className="row gap-10 wrap">
            <Badge tone="muted">{titleCase(customer.kind)}</Badge>
            <span className="dim mono">{customer.code}</span>
            <span className="dim">{customer.terms?.toUpperCase()}</span>
            <span className="dim">{customer.rateCard} rate card</span>
            {customer.accountOwner ? <span className="dim">Owner {customer.accountOwner}</span> : null}
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" icon={UserPlus} onClick={() => setContactOpen(true)}>Add contact</Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate('/app/jobs?new=1')}>Add a job</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Billed · 30d" icon={Wallet} value={compactMoney(kpis?.revenue30)} tone="amber" foot={`${compactMoney(kpis?.revenue90)} over 90 days`} />
        <Stat label="Margin · 90d" icon={Banknote} value={compactMoney(kpis?.margin90)} tone="green" foot={`${money(kpis?.avgJob)} average job`} />
        <Stat label="Open A/R" icon={Receipt} value={compactMoney(kpis?.openBalance)} tone={Number(kpis?.openBalance) > 0 ? 'red' : 'green'} foot={`${invoices.filter((i) => i.status !== 'paid').length} open invoices`} />
        <Stat
          label="Service level"
          icon={Truck}
          value={kpis?.unfilled ? 'At risk' : 'Covered'}
          tone={kpis?.unfilled ? 'red' : 'lime'}
          foot={`${kpis.unfilled} unfilled · ${num(kpis.jobsDone)} delivered`}
          size="sm"
        />
      </div>

      <Tabs
        className="mb-20"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'overview', label: 'Overview', icon: Building2 },
          { value: 'sites', label: 'Facilities', icon: MapPin, count: sites.length },
          { value: 'jobs', label: 'Jobs', icon: Truck, count: jobs.length },
          { value: 'billing', label: 'Billing', icon: Receipt, count: invoices.length },
        ]}
      />

      {tab === 'overview' ? (
        <div className="split-wide">
          <div className="col gap-20">
            <Card>
              <CardHead title="Billing history" subtitle="Monthly billings and margin" icon={Wallet} />
              <CardBody>
                <AreaChart
                  labels={(monthly || []).map((m) => m.month)}
                  series={[
                    { key: 'revenue', label: 'Billed', color: '#ff8a1f', values: (monthly || []).map((m) => m.revenue) },
                    { key: 'margin', label: 'Margin', color: '#34d399', values: (monthly || []).map((m) => m.margin) },
                  ]}
                  height={220}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Jobs we did for them" icon={Truck} actions={<Link to={`/app/jobs?customerId=${customer.id}`}><Button size="sm" variant="ghost">View all</Button></Link>} />
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Site</th>
                      <th>Lumper</th>
                      <th className="narrow">Window</th>
                      <th className="narrow">Status</th>
                      <th className="right">Bill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.slice(0, 10).map((job) => (
                      <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                        <td className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{job.code}</td>
                        <td className="truncate">{job.siteName}</td>
                        <td className="truncate">{job.lumperName || <span className="dim">Unassigned</span>}</td>
                        <td className="narrow num">{dateLabel(job.windowStart)}</td>
                        <td className="narrow"><JobStatusBadge status={job.status} size="sm" dot={false} /></td>
                        <td className="right num">{money(job.customerBill)}</td>
                      </tr>
                    ))}
                    {!jobs.length ? <tr><td colSpan={6}><EmptyState icon={Truck} title="No work orders yet" /></td></tr> : null}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="col gap-20">
            <Card>
              <CardHead title="Account" icon={Building2} />
              <CardBody>
                {customer.address ? (
                  <div className="row-top gap-8 mb-12">
                    <MapPin size={14} style={{ color: 'var(--amber)', marginTop: 3 }} />
                    <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                      {customer.address}<br />{customer.city}, {customer.state} {customer.zip}
                    </span>
                  </div>
                ) : null}
                <KeyValue label="Terms">{customer.terms?.toUpperCase()}</KeyValue>
                <KeyValue label="Credit limit">{money(customer.creditLimit)}</KeyValue>
                <KeyValue label="Rate card"><span className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{customer.rateCard}</span></KeyValue>
                <KeyValue label="Sites">{num(customer.siteCount)}</KeyValue>
                <KeyValue label="Status"><Badge tone="green" size="sm">{titleCase(customer.status)}</Badge></KeyValue>
                {customer.notes ? <p className="muted mt-12" style={{ fontSize: 'var(--fs-sm)' }}>{customer.notes}</p> : null}
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Contacts" icon={Phone} actions={<Button size="sm" variant="ghost" onClick={() => setContactOpen(true)}>Add</Button>} />
              <CardBody className="col gap-12">
                {contacts.map((contact) => (
                  <div key={contact.id} className="row gap-10">
                    <Avatar name={contact.name} tone={contact.isPrimary ? 'amber' : 'muted'} size="sm" />
                    <div className="grow truncate">
                      <div className="row gap-6">
                        <span className="truncate" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{contact.name}</span>
                        {contact.isPrimary ? <Badge tone="amber" size="sm">Primary</Badge> : null}
                      </div>
                      <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{contact.title}</span>
                      <span className="dim truncate mono" style={{ fontSize: 'var(--fs-xs)' }}>{contact.email}</span>
                    </div>
                    <a href={`tel:${phoneDial(contact.phone)}`} className="btn btn-ghost btn-icon" aria-label="Call"><Phone size={14} /></a>
                  </div>
                ))}
                {!contacts.length ? <span className="dim">No contacts on file yet.</span> : null}
              </CardBody>
            </Card>

            {disputes.length ? (
              <Card>
                <CardHead title="Disputes & claims" icon={Receipt} />
                <CardBody className="col gap-10">
                  {disputes.map((dispute) => (
                    <div key={dispute.id} className="tile">
                      <div className="row between gap-8">
                        <span className="row gap-6">
                          <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{dispute.code}</span>
                          <Badge tone={dispute.status === 'open' ? 'red' : 'muted'} size="sm">{titleCase(dispute.category)}</Badge>
                        </span>
                        <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{money(dispute.amountClaimed)}</span>
                      </div>
                      <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{dispute.body}</p>
                      <span className="dimmer" style={{ fontSize: 10 }}>
                        {relative(dispute.openedAt)}{dispute.jobCode ? ` · ${dispute.jobCode}` : ''}
                      </span>
                    </div>
                  ))}
                </CardBody>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'sites' ? (
        <>
          <div className="row between wrap gap-12 mb-12">
            <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
              Every dock, warehouse and yard we work for {customer.name}.
            </span>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setSiteOpen(true)}>Add a facility</Button>
          </div>
          <div className="grid grid-auto stagger">
            {sites.map((site) => (
            <Card key={site.id} className="tile" style={{ padding: 0 }}>
              <CardBody>
                <div className="row between gap-12">
                  <div className="grow truncate">
                    <Link to={`/app/sites/${site.id}`} style={{ fontWeight: 620 }}>{site.name}</Link>
                    <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{site.address}</div>
                    <div className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{site.city}, {site.state} {site.zip}</div>
                  </div>
                  {site.openJobs ? <Badge tone="amber" dot>{site.openJobs} open</Badge> : null}
                </div>
                <div className="divider" />
                <div className="row gap-16 wrap">
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>Dock {site.dockHours}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{site.dockDoors} doors</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{site.appointmentRequired ? 'Appointment required' : 'Drop in'}</span>
                  {site.avgWaitMinutes ? <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>~{Math.round(site.avgWaitMinutes)} min wait</span> : null}
                </div>
              </CardBody>
            </Card>
            ))}
            {!sites.length ? (
              <Card>
                <EmptyState
                  icon={MapPin}
                  title="No facilities on file"
                  action={<Button size="sm" variant="primary" icon={Plus} onClick={() => setSiteOpen(true)}>Add a facility</Button>}
                />
              </Card>
            ) : null}
          </div>
        </>
      ) : null}

      {tab === 'jobs' ? (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Site</th>
                  <th>Lumper</th>
                  <th className="narrow">Window</th>
                  <th className="narrow">Status</th>
                  <th className="right">Pallets</th>
                  <th className="right">Hours</th>
                  <th className="right">Bill</th>
                  <th className="right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                    <td className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{job.code}</td>
                    <td className="truncate">{job.siteName}</td>
                    <td className="truncate">{job.lumperName || <span className="dim">Unassigned</span>}</td>
                    <td className="narrow num">{dateLabel(job.windowStart)}</td>
                    <td className="narrow"><JobStatusBadge status={job.status} size="sm" dot={false} /></td>
                    <td className="right num">{job.palletsActual || job.palletsExpected}</td>
                    <td className="right num">{job.billableHours ? Number(job.billableHours).toFixed(1) : '—'}</td>
                    <td className="right num">{money(job.customerBill)}</td>
                    <td className="right num" style={{ color: 'var(--green)' }}>{money(job.margin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {tab === 'billing' ? (
        <Card>
          <CardHead title="Invoices" subtitle="Newest first" icon={Receipt} actions={<Link to="/app/invoices"><Button size="sm" variant="ghost">Invoice workspace</Button></Link>} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th className="narrow">Issued</th>
                  <th className="narrow">Due</th>
                  <th className="narrow">Status</th>
                  <th className="narrow">Ageing</th>
                  <th className="right">Jobs</th>
                  <th className="right">Total</th>
                  <th className="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="clickable" onClick={() => navigate(`/app/invoices/${invoice.id}`)}>
                    <td className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{invoice.code}</td>
                    <td className="narrow num">{dateLabel(invoice.issuedAt)}</td>
                    <td className="narrow num">{dateLabel(invoice.dueAt)}</td>
                    <td className="narrow">
                      <Badge size="sm" tone={invoice.status === 'paid' ? 'green' : invoice.status === 'overdue' ? 'red' : 'blue'}>
                        {titleCase(invoice.status)}
                      </Badge>
                    </td>
                    <td className="narrow">
                      <Badge size="sm" tone={BUCKET_TONE[invoice.agingBucket] || 'muted'}>{invoice.agingBucket}</Badge>
                    </td>
                    <td className="right num">{invoice.jobsCount}</td>
                    <td className="right num">{money(invoice.total)}</td>
                    <td className="right num" style={{ color: Number(invoice.balance) > 0 ? 'var(--amber)' : undefined }}>
                      {money(invoice.balance)}
                    </td>
                  </tr>
                ))}
                {!invoices.length ? <tr><td colSpan={8}><EmptyState icon={Receipt} title="Nothing invoiced yet" /></td></tr> : null}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <AddContactModal
        open={contactOpen}
        customerId={id}
        onClose={() => setContactOpen(false)}
        onSaved={() => {
          setContactOpen(false);
          refresh();
          toast.ok('Contact added');
        }}
      />

      <AddSiteModal
        open={siteOpen}
        customers={[{ id: customer.id, name: customer.name }]}
        defaultCustomerId={customer.id}
        onClose={() => setSiteOpen(false)}
        onCreated={(site) => {
          toast.ok(`${site.name} added`, 'We are looking the address up for the map.');
          refresh();
          navigate(`/app/sites/${site.id}`);
        }}
      />
    </>
  );
}

function AddContactModal({ open, customerId, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', title: '', email: '', phone: '', isPrimary: false });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a contact"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              if (!form.name) {
                toast.err('A contact name is required');
                return;
              }
              setBusy(true);
              try {
                await api.post(`/customers/${customerId}/contacts`, form);
                setForm({ name: '', title: '', email: '', phone: '', isPrimary: false });
                onSaved();
              } catch (err) {
                toast.err('Could not add the contact', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add contact
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Name" required><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Title"><Input value={form.title} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="Email"><Input value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Phone"><PhoneInput value={form.phone} onChange={(value) => set('phone', value)} /></Field>
      </div>
      <label className="check mt-16">
        <input type="checkbox" checked={form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} />
        <span>Primary contact for this account</span>
      </label>
    </Modal>
  );
}
